<!--
  OrderLtvView.vue — LTV 报告页面
  基于首购日期的 Cohort LTV 分析，展示 M0~Mn 各阶段订单、收入及留存率。
  功能：日期范围筛选、应用筛选、分页查询、CSV 下载。
  接口：GET /v1/analytics/order/stats/cohort/daily/report
  特点：列数动态——根据当页数据的 maxMNo 自动生成 M1~Mn 的订单数/收入/留存率列。
-->
<template>
  <div>
    <!-- 搜索栏 -->
    <a-card
      :bordered="false"
      style="margin-bottom: 16px"
    >
      <a-form
        layout="inline"
        :model="searchForm"
      >
        <a-form-item label="开始日期">
          <a-date-picker
            v-model:value="startDateVal"
            value-format="YYYY-MM-DD"
            placeholder="请选择开始日期"
            :allow-clear="true"
          />
        </a-form-item>
        <a-form-item label="结束日期">
          <a-date-picker
            v-model:value="endDateVal"
            value-format="YYYY-MM-DD"
            placeholder="请选择结束日期"
            :allow-clear="true"
          />
        </a-form-item>
        <a-form-item label="应用">
          <a-select
            v-model:value="searchForm.appId"
            style="width: 180px"
            placeholder="全部应用"
            allow-clear
            :options="appOptions"
          />
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button
              type="primary"
              :loading="loading"
              @click="handleSearch"
            >查询</a-button>
            <a-button @click="handleReset">重置</a-button>
            <a-button
              :loading="downloading"
              @click="handleDownload"
            >下载</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- 数据表格 -->
    <a-card
      :bordered="false"
      class="ltv-report-card"
    >
      <template #title>
        <span
          class="ltv-collapse-toggle"
          @click="reportCollapsed = !reportCollapsed"
        >
          <CaretDownOutlined
            v-if="!reportCollapsed"
            style="margin-right: 6px; font-size: 12px"
          />
          <CaretRightOutlined
            v-else
            style="margin-right: 6px; font-size: 12px"
          />
          LTV订单报告
        </span>
      </template>
      <div v-show="!reportCollapsed">
        <a-table
          :columns="columns"
          :data-source="tableData"
          :loading="loading"
          :pagination="pagination"
          row-key="rowKey"
          :scroll="{ x: 'max-content' }"
          size="small"
          bordered
          class="ltv-table"
          @change="handleTableChange"
        />
      </div>
    </a-card>

    <!-- 预估回本模块（仅管理员可见） -->
    <a-card
      v-if="isAdmin() && searchForm.appId != null"
      :bordered="false"
      class="estimate-card"
      style="margin-top: 16px"
    >
      <template #title>
        <span
          class="ltv-collapse-toggle"
          @click="estimateCollapsed = !estimateCollapsed"
        >
          <CaretDownOutlined
            v-if="!estimateCollapsed"
            style="margin-right: 6px; font-size: 12px"
          />
          <CaretRightOutlined
            v-else
            style="margin-right: 6px; font-size: 12px"
          />
          预估回本分析
        </span>
      </template>
      <div v-show="!estimateCollapsed">
        <!-- 留存率表 -->
        <div style="margin-bottom: 8px">
          <a-button
            size="small"
            :loading="retentionSaving"
            @click="handleSaveRetentionRates"
          >保存留存率</a-button>
          <a-button
            size="small"
            style="margin-left: 8px"
            :loading="retentionRateLoading"
            @click="fetchRetentionRates"
          >重新加载</a-button>
        </div>
        <div style="margin-bottom: 16px; overflow-x: auto">
          <table
            class="estimate-table"
            border="1"
            cellspacing="0"
            cellpadding="4"
          >
            <thead>
              <tr>
                <th>订单分成收入单价</th>
                <th
                  v-for="n in 16"
                  :key="n - 1"
                >M{{ n - 1 }}留存率</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="text-align: center">{{ estFirstPrice }}</td>
                <td
                  v-for="n in 16"
                  :key="n - 1"
                  style="text-align: center; white-space: nowrap"
                >
                  <a-input-number
                    :value="estRetentionRates[n - 1] != null ? +(estRetentionRates[n - 1] * 100).toFixed(2) : null"
                    @update:value="val => estRetentionRates[n - 1] = val != null ? val / 100 : null"
                    :min="0"
                    :max="100"
                    :step="0.1"
                    size="small"
                    style="width: 80px"
                  />%
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 输入参数 -->
        <div style="margin-bottom: 8px; display: flex; flex-wrap: wrap; gap: 16px; align-items: center">
          <span>
            投放首次价格(元)：
            <a-input-number
              v-model:value="estFirstPrice"
              :min="0"
              :step="0.1"
              style="width: 120px"
            />
          </span>
          <span>
            原始获客成本(元)：
            <a-input-number
              v-model:value="estAcquisitionCost"
              :min="0"
              :step="0.1"
              style="width: 120px"
            />
          </span>

        </div>

        <!-- 实时计算表格 -->
        <div style="overflow-x: auto">
          <table
            class="estimate-table"
            border="1"
            cellspacing="0"
            cellpadding="4"
          >
            <thead>
              <tr>
                <th></th>
                <th
                  v-for="n in (estMaxM + 1)"
                  :key="n - 1"
                >M{{ n - 1 }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><b>收入</b></td>
                <td
                  v-for="n in (estMaxM + 1)"
                  :key="n - 1"
                  style="text-align: right"
                >{{ estCalcData.revenue[n - 1]?.toFixed(2) ?? '-' }}</td>
              </tr>
              <tr>
                <td><b>累计收入</b></td>
                <td
                  v-for="n in (estMaxM + 1)"
                  :key="n - 1"
                  style="text-align: right"
                >{{ estCalcData.cumulativeRevenue[n - 1]?.toFixed(2) ?? '-' }}</td>
              </tr>
              <tr>
                <td><b>当期业务毛利</b></td>
                <td
                  v-for="n in (estMaxM + 1)"
                  :key="n - 1"
                  style="text-align: right"
                  :style="{ color: (estCalcData.margin[n - 1] ?? 0) < 0 ? 'red' : '' }"
                >{{ estCalcData.margin[n - 1]?.toFixed(2) ?? '-' }}</td>
              </tr>
              <tr>
                <td><b>累计业务毛利</b></td>
                <td
                  v-for="n in (estMaxM + 1)"
                  :key="n - 1"
                  style="text-align: right"
                  :style="{ color: (estCalcData.cumulativeMargin[n - 1] ?? 0) < 0 ? 'red' : '' }"
                >{{ estCalcData.cumulativeMargin[n - 1]?.toFixed(2) ?? '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 回本结论 -->
        <div style="margin-top: 12px; font-size: 14px">
          <b>预估回本：</b>
          <span
            v-if="estBreakevenMonth != null"
            style="color: green"
          >
            M{{ estBreakevenMonth }} 累计业务毛利转正（ROI≥1）
          </span>
          <span
            v-else
            style="color: red"
          >
            M{{ estMaxM }} 内尚未回本
          </span>
        </div>
      </div>
    </a-card>

    <!-- LTV 留存率热力表 -->
    <a-card
      v-if="searchForm.appId != null"
      :bordered="false"
      class="retention-card"
      style="margin-top: 16px"
    >
      <template #title>
        留存热力分析
        <a-radio-group
          v-model:value="retentionGroupBy"
          size="small"
          button-style="solid"
          style="margin-left: 12px"
        >
          <a-radio-button value="day">日</a-radio-button>
          <a-radio-button value="week">周</a-radio-button>
          <a-radio-button value="month">月</a-radio-button>
        </a-radio-group>
        <span class="retention-center-title">+n 留存率</span>
      </template>
      <a-spin :spinning="retentionLoading">
        <div class="retention-table-wrap">
          <table class="retention-table">
            <thead>
              <tr>
                <th class="rt-th rt-fixed">日期</th>
                <th class="rt-th">M0订单</th>
                <th
                  class="rt-th"
                  v-for="n in retentionMaxM"
                  :key="n"
                >M{{ n }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in retentionRows"
                :key="idx"
              >
                <td class="rt-td rt-fixed">{{ row.label }}</td>
                <td class="rt-td">{{ row.m0Count }}</td>
                <td
                  class="rt-td"
                  v-for="n in retentionMaxM"
                  :key="n"
                  :style="retentionCellStyle(row.rates[n])"
                >
                  <template v-if="row.rates[n] != null && row.rates[n] !== 0">
                    <div class="rt-rate">{{ row.rates[n] }}%</div>
                    <div class="rt-sub">{{ row.counts[n] ?? 0 }}</div>
                  </template>
                </td>
              </tr>
              <tr v-if="!retentionRows.length && !retentionLoading">
                <td
                  class="rt-td"
                  :colspan="retentionMaxM + 2"
                  style="color: #999; padding: 24px"
                >暂无数据</td>
              </tr>
            </tbody>
          </table>
        </div>
      </a-spin>
    </a-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import { message } from "ant-design-vue";
import { CaretRightOutlined, CaretDownOutlined } from "@ant-design/icons-vue";
import { apiUrl, analyticsUrl } from "@/utils/api";
import { get, postJson } from "@/utils/request";
import { isAdmin } from "@/utils/permission";

/** 表格加载状态 */
const loading = ref(false);
/** CSV 下载加载状态 */
const downloading = ref(false);
/** 日期范围选择器绑定值 */
const startDateVal = ref(null);
const endDateVal = ref(null);
/** 表格数据 */
const tableData = ref([]);
/** 应用下拉选项 */
const appOptions = ref([]);
/** 应用 ID → 应用名称映射，用于补充响应中缺少的 appName */
const appNameMap = ref({});

/** Ant Design 表格分页配置 */
const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  pageSizeOptions: ["10", "20", "50", "100", "300"],
  showTotal: (total) => `共 ${total} 条`,
});

/** 报告列表是否折叠 */
const reportCollapsed = ref(false);

/** 搜索表单数据模型 */
const LTV_APP_ID_CACHE_KEY = "orderLtvView.appId";

/** 从 localStorage 读取上次选择的应用 ID */
const loadCachedAppId = () => {
  try {
    const raw = localStorage.getItem(LTV_APP_ID_CACHE_KEY);
    if (raw == null || raw === "") return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  } catch (e) {
    return undefined;
  }
};

const searchForm = reactive({
  startDate: "",
  endDate: "",
  appId: loadCachedAppId(),
});

/** 选择应用变化时写入缓存 */
watch(
  () => searchForm.appId,
  (val) => {
    try {
      if (val == null) localStorage.removeItem(LTV_APP_ID_CACHE_KEY);
      else localStorage.setItem(LTV_APP_ID_CACHE_KEY, String(val));
    } catch (e) {
      // 忽略存储异常
    }
  }
);

// ========================
// 留存热力图
// ========================

const RETENTION_GROUP_KEY = "orderLtvView.retentionGroup";
const retentionLoading = ref(false);
const retentionRawData = ref([]);
const retentionGroupBy = ref(
  localStorage.getItem(RETENTION_GROUP_KEY) || "day"
);

watch(retentionGroupBy, (val) => {
  try {
    localStorage.setItem(RETENTION_GROUP_KEY, val);
  } catch (e) {
    // 忽略
  }
});

/** 计算所有行中最大的 mNo */
const retentionMaxM = computed(() =>
  retentionRawData.value.reduce(
    (max, r) => Math.max(max, ...(r.stages ?? []).map((s) => s.mNo)),
    0
  )
);

/** 周一日期 */
const getMonday = (dateStr) => {
  const d = new Date(dateStr);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
};

/** 每日原始行 → 统一结构 */
const retentionDailyRows = computed(() =>
  [...retentionRawData.value].reverse().map((r) => {
    const counts = {};
    const rates = {};
    for (const s of r.stages ?? []) {
      if (s.mNo >= 1) {
        counts[s.mNo] = s.orderCount ?? 0;
        const val = parseFloat(s.retentionRate);
        rates[s.mNo] = isNaN(val) ? null : +(val * 100).toFixed(1);
      }
    }
    return {
      statDate: r.statDate,
      label: r.statDate,
      m0Count: r.m0OrderCount ?? 0,
      counts,
      rates,
    };
  })
);

/** 周/月分组聚合 */
const groupRetentionRows = (rows, keyFn, labelFn) => {
  const map = new Map();
  for (const r of rows) {
    const k = keyFn(r.statDate);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(r);
  }
  const result = [];
  for (const [, group] of map) {
    const m0Sum = group.reduce((s, r) => s + r.m0Count, 0);
    const maxN = retentionMaxM.value;
    const counts = {};
    const rates = {};
    for (let n = 1; n <= maxN; n++) {
      const cntSum = group.reduce((s, r) => s + (r.counts[n] ?? 0), 0);
      counts[n] = cntSum;
      const hasData = group.some((r) => r.counts[n] != null);
      // 周/月留存率均以 M0 订单数为分母
      const base = m0Sum;
      rates[n] =
        hasData && base > 0 ? +((cntSum / base) * 100).toFixed(1) : null;
    }
    result.push({ label: labelFn(group), m0Count: m0Sum, counts, rates });
  }
  return result;
};

const retentionRows = computed(() => {
  const daily = retentionDailyRows.value;
  let rows;
  if (retentionGroupBy.value === "week") {
    rows = groupRetentionRows(
      daily,
      (d) => getMonday(d),
      (g) => {
        const mon = getMonday(g[0].statDate);
        const sun = new Date(mon);
        sun.setDate(sun.getDate() + 6);
        return mon + "~" + sun.toISOString().slice(0, 10);
      }
    );
  } else if (retentionGroupBy.value === "month") {
    rows = groupRetentionRows(
      daily,
      (d) => d.slice(0, 7),
      (g) => g[0].statDate.slice(0, 7)
    );
  } else {
    rows = daily;
  }
  return rows.filter(
    (r) =>
      r.m0Count > 0 || Object.values(r.rates).some((v) => v != null && v !== 0)
  );
});

/** 热力单元格背景色 */
const retentionCellStyle = (rate) => {
  if (rate == null || rate === 0) return {};
  const alpha = Math.min(0.05 + (rate / 100) * 0.55, 0.65);
  const textColor = alpha > 0.35 ? "#fff" : "#333";
  return {
    background: `rgba(24, 144, 255, ${alpha})`,
    color: textColor,
  };
};

/** 拉取留存热力数据：复用同一接口, 拉取与当前筛选一致的全量分页数据 */
const fetchRetentionData = async () => {
  if (searchForm.appId == null) {
    retentionRawData.value = [];
    return;
  }
  retentionLoading.value = true;
  try {
    const baseParams = {
      startDate: searchForm.startDate || "",
      endDate: searchForm.endDate || "",
      appId: searchForm.appId,
    };
    const PAGE_SIZE = 300;
    const url = analyticsUrl("/order/stats/cohort/daily/report");
    const first = await get(url, {
      ...baseParams,
      pageIdx: 1,
      pageSize: PAGE_SIZE,
    });
    if (first.code !== 200 || !first.data) {
      retentionRawData.value = [];
      return;
    }
    const all = [...(first.data.data ?? [])];
    const pageCount = first.data.pageCount ?? 1;
    if (pageCount > 1) {
      const rest = await Promise.all(
        Array.from({ length: pageCount - 1 }, (_, i) =>
          get(url, { ...baseParams, pageIdx: i + 2, pageSize: PAGE_SIZE })
        )
      );
      for (const json of rest) {
        if (json.code === 200 && json.data?.data) {
          all.push(...json.data.data);
        }
      }
    }
    retentionRawData.value = all;
  } catch (e) {
    message.error("留存数据获取失败");
  } finally {
    retentionLoading.value = false;
  }
};

/**
 * 获取应用列表（填充下拉选择器 & 构建 appNameMap）
 * 接口：GET /v1/product/all
 */
const fetchApps = async () => {
  try {
    const json = await get(apiUrl("/v1/product/all?available=1"));
    const list = Array.isArray(json) ? json : json.data;
    if (Array.isArray(list)) {
      appOptions.value = list.map((a) => ({ label: a.name, value: a.id }));
      appNameMap.value = Object.fromEntries(list.map((a) => [a.id, a.name]));
    }
  } catch (e) {
    // 静默失败，不影响主功能
  }
};

/**
 * 金额格式化：分 → 元
 * @param {number|null} fen - 金额（单位：分）
 * @returns {string} 格式化后的金额字符串，如 "¥1,234.56"
 */
const formatAmount = (fen) => {
  if (fen == null) return "-";
  return (
    "¥" +
    (fen / 100).toLocaleString("zh-CN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
};

/**
 * 率值格式化：小数 → 百分比
 * @param {string|null} rate - 率值字符串（如 "0.123456"）
 * @returns {string} 格式化后的百分比，如 "12.35%"
 */
const formatRate = (rate) => {
  if (rate == null) return "-";
  const val = parseFloat(rate);
  if (isNaN(val)) return "-";
  return (val * 100).toFixed(2) + "%";
};

/** 单元格不换行样式 */
const nowrap = { style: { whiteSpace: "nowrap" } };

/**
 * 从 stages 数组中查找指定 M 阶段的数据
 * @param {Array} stages - 阶段明细数组
 * @param {number} mNo - M 阶段编号
 * @returns {Object|null}
 */
const getStage = (stages, mNo) =>
  (stages ?? []).find((s) => s.mNo === mNo) ?? null;

/** 当前页数据中最大的 maxMNo，决定动态列数量 */
const pageMaxMNo = computed(() =>
  tableData.value.reduce((max, r) => Math.max(max, r.maxMNo ?? 0), 0)
);

/**
 * 表格列定义（动态计算属性）
 * 基础列：首购日期、应用、总收款、M0数据、退款、手续费、净额等
 * 动态列：根据 pageMaxMNo 生成 M1~Mn 的订单数/收入/留存率列
 */
const columns = computed(() => {
  const base = [
    {
      title: "首购日期",
      dataIndex: "statDate",
      key: "statDate",
      width: 110,
      fixed: "left",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
    },
    {
      title: "应用",
      dataIndex: "appName",
      key: "appName",
      width: 150,
      fixed: "left",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
    },
    {
      title: "总收款",
      dataIndex: "totalOrderAmount",
      key: "totalOrderAmount",
      width: 120,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "M0首购单数",
      dataIndex: "m0OrderCount",
      key: "m0OrderCount",
      width: 100,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => text?.toLocaleString() ?? "-",
    },
    {
      title: "M0收入",
      dataIndex: "m0OrderAmount",
      key: "m0OrderAmount",
      width: 110,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "退款单数",
      dataIndex: "refundOrderCount",
      key: "refundOrderCount",
      width: 90,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => text?.toLocaleString() ?? "-",
    },
    {
      title: "退款金额",
      dataIndex: "refundAmount",
      key: "refundAmount",
      width: 120,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "退款率",
      dataIndex: "refundRate",
      key: "refundRate",
      width: 90,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatRate(text),
    },
    {
      title: "收款手续费",
      dataIndex: "collectFee",
      key: "collectFee",
      width: 110,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "退款手续费",
      dataIndex: "refundFee",
      key: "refundFee",
      width: 110,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "手续费",
      dataIndex: "serviceFee",
      key: "serviceFee",
      width: 100,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "预估通道费",
      dataIndex: "estimatedChannelFee",
      key: "estimatedChannelFee",
      width: 120,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
    {
      title: "净额",
      dataIndex: "netAmount",
      key: "netAmount",
      width: 120,
      align: "center",
      customCell: () => nowrap,
      customHeaderCell: () => nowrap,
      customRender: ({ text }) => formatAmount(text),
    },
  ];

  const max = pageMaxMNo.value;
  if (max < 1) return base;

  const stageCols = [];
  for (let n = 1; n <= max; n++) {
    const mn = n;
    stageCols.push(
      {
        title: `M${mn}订单数`,
        key: `m${mn}Count`,
        width: 100,
        align: "center",
        customCell: () => nowrap,
        customHeaderCell: () => nowrap,
        customRender: ({ record }) => {
          const s = getStage(record.stages, mn);
          return s != null ? s.orderCount.toLocaleString() : "-";
        },
      },
      {
        title: `M${mn}收入`,
        key: `m${mn}Amount`,
        width: 120,
        align: "center",
        customCell: () => nowrap,
        customHeaderCell: () => nowrap,
        customRender: ({ record }) => {
          const s = getStage(record.stages, mn);
          return s != null ? formatAmount(s.orderAmount) : "-";
        },
      },
      {
        title: `M${mn}留存率`,
        key: `m${mn}Rate`,
        width: 100,
        align: "center",
        customCell: () => nowrap,
        customHeaderCell: () => nowrap,
        customRender: ({ record }) => {
          const s = getStage(record.stages, mn);
          return s != null ? formatRate(s.retentionRate) : "-";
        },
      }
    );
  }

  return [...base, ...stageCols];
});

/**
 * 获取 LTV 报告数据
 * @param {number} pageIdx - 页码
 * @param {number} pageSize - 每页条数
 */
const fetchData = async (pageIdx = 1, pageSize = 20) => {
  loading.value = true;
  try {
    const params = {
      pageIdx,
      pageSize,
      startDate: searchForm.startDate || "",
      endDate: searchForm.endDate || "",
    };
    if (searchForm.appId != null) params.appId = searchForm.appId;
    const json = await get(
      analyticsUrl("/order/stats/cohort/daily/report"),
      params
    );
    if (json.code === 200 && json.data) {
      const d = json.data;
      tableData.value = d.data.map((r, i) => ({
        ...r,
        appName: appNameMap.value[r.appId] || `App ${r.appId}`,
        rowKey: `${r.statDate}_${r.appId}_${i}`,
      }));
      pagination.total = d.dataSum;
      pagination.current = d.pageIdx;
      pagination.pageSize = d.pageSize;
    } else {
      message.warning("查询失败：" + (json.content || "未知错误"));
    }
  } catch (e) {
    message.error("接口请求失败");
  } finally {
    loading.value = false;
  }
};

/** 查询按钮：同步日期范围和应用筛选 */
const handleSearch = () => {
  searchForm.startDate = startDateVal.value || "";
  searchForm.endDate = endDateVal.value || "";
  fetchData(1, pagination.pageSize);
  fetchRetentionData();
};

/** 重置筛选条件并重新加载 */
const handleReset = () => {
  startDateVal.value = null;
  endDateVal.value = null;
  searchForm.startDate = "";
  searchForm.endDate = "";
  searchForm.appId = undefined;
  fetchData(1, pagination.pageSize);
  fetchRetentionData();
};

/** 表格分页变化回调 */
const handleTableChange = (pag) => {
  fetchData(pag.current, pag.pageSize);
};

/**
 * CSV 下载
 * 遍历所有分页数据，平展 stages 为 M1~Mn 列，拼接 CSV 并触发下载。
 * globalMaxMNo 用于统一所有行的列数。
 */
const handleDownload = async () => {
  downloading.value = true;
  try {
    const allData = [];
    let pageIdx = 1;
    let pageCount = 1;
    let globalMaxMNo = 0;
    while (pageIdx <= pageCount) {
      const params = {
        pageIdx,
        pageSize: 100,
        startDate: searchForm.startDate || "",
        endDate: searchForm.endDate || "",
      };
      if (searchForm.appId != null) params.appId = searchForm.appId;
      const json = await get(
        analyticsUrl("/order/stats/cohort/daily/report"),
        params
      );
      if (json.code !== 200 || !json.data) break;
      const rows = json.data.data.map((r) => ({
        ...r,
        appName: appNameMap.value[r.appId] || `App ${r.appId}`,
      }));
      allData.push(...rows);
      for (const r of rows)
        globalMaxMNo = Math.max(globalMaxMNo, r.maxMNo ?? 0);
      pageCount = json.data.pageCount;
      pageIdx++;
    }
    if (!allData.length) {
      message.warning("没有数据可下载");
      return;
    }

    const amountFields = [
      "totalOrderAmount",
      "m0OrderAmount",
      "refundAmount",
      "collectFee",
      "refundFee",
      "serviceFee",
      "estimatedChannelFee",
      "netAmount",
    ];
    const baseHeaders = [
      "首购日期",
      "应用",
      "总收款",
      "M0首购单数",
      "M0收入",
      "退款单数",
      "退款金额",
      "退款率",
      "收款手续费",
      "退款手续费",
      "手续费",
      "预估通道费",
      "净额",
    ];
    const baseKeys = [
      "statDate",
      "appName",
      "totalOrderAmount",
      "m0OrderCount",
      "m0OrderAmount",
      "refundOrderCount",
      "refundAmount",
      "refundRate",
      "collectFee",
      "refundFee",
      "serviceFee",
      "estimatedChannelFee",
      "netAmount",
    ];

    const stageHeaders = [];
    for (let n = 1; n <= globalMaxMNo; n++) {
      stageHeaders.push(`M${n}订单数`, `M${n}收入`, `M${n}留存率`);
    }

    const esc = (v) => {
      const s = String(v ?? "");
      return s.includes(",") || s.includes('"') || s.includes("\n")
        ? '"' + s.replace(/"/g, '""') + '"'
        : s;
    };

    const header = [...baseHeaders, ...stageHeaders].map(esc).join(",");
    const rows = allData.map((r) => {
      const baseCells = baseKeys.map((k) => {
        const v = r[k];
        if (v == null) return "";
        if (amountFields.includes(k)) return (v / 100).toFixed(2);
        if (k === "refundRate") {
          const val = parseFloat(v);
          return isNaN(val) ? "" : (val * 100).toFixed(2) + "%";
        }
        return v;
      });
      const stageCells = [];
      for (let n = 1; n <= globalMaxMNo; n++) {
        const s = getStage(r.stages, n);
        if (s) {
          stageCells.push(
            s.orderCount,
            (s.orderAmount / 100).toFixed(2),
            (parseFloat(s.retentionRate) * 100).toFixed(2) + "%"
          );
        } else {
          stageCells.push("", "", "");
        }
      }
      return [...baseCells, ...stageCells].map(esc).join(",");
    });
    const csv = "\uFEFF" + [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `LTV报告.csv`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    message.error("下载失败");
  } finally {
    downloading.value = false;
  }
};

/** 组件挂载：先加载应用列表（用于名称映射），再加载第一页数据 */
onMounted(async () => {
  await fetchApps();
  fetchData();
  fetchRetentionData();
  if (isAdmin() && searchForm.appId != null) {
    fetchRetentionRates();
  }
});

// ========================
// 预估回本分析模块
// ========================

/** 折叠状态 */
const estimateCollapsed = ref(false);
/** 留存率加载/保存状态 */
const retentionRateLoading = ref(false);
const retentionSaving = ref(false);

/** 投放首次价格（元），可编辑 */
const estFirstPrice = ref(38.8);
/** 原始获客成本（元），可编辑 */
const estAcquisitionCost = ref(0);

/**
 * 留存率表 — 从接口获取，可手动修改
 * 值为小数形式 (如 0.95 表示 95%)
 */
const estRetentionRates = reactive({});

/** 预估回本最大M值，跟随主表 */
const estMaxM = computed(() => {
  // 按留存率表的最大有效 M 下标确定，最少显示到 M1，最多到 M15
  for (let i = 15; i >= 1; i--) {
    if (estRetentionRates[i] != null) return i;
  }
  return 15;
});

/** M1留存率（百分比数值，自动计算，不对外暴露） */
const estM1Retention = ref(null);

/** 当前页 stage 汇总（按 mNo） */
const estStageSums = computed(() => {
  const result = {};
  const max = pageMaxMNo.value;
  for (let n = 0; n <= max; n++) {
    let countSum = 0;
    let amountSum = 0;
    for (const r of tableData.value) {
      const s = getStage(r.stages, n);
      if (s) {
        countSum += s.orderCount ?? 0;
        amountSum += s.orderAmount ?? 0;
      }
    }
    result[n] = { count: countSum, amount: amountSum };
  }
  return result;
});

/** 自动计算 M1 留存率 */
const calcM1Retention = () => {
  const ss = estStageSums.value;
  const m0Count = ss[0]?.count ?? 0;
  const m1Count = ss[1]?.count ?? 0;
  if (m0Count === 0) return null;
  return +((m1Count / m0Count) * 100).toFixed(2);
};

/** 实时计算表格（M0~Mn） */
const estCalcData = computed(() => {
  const rates = estRetentionRates;
  const revenue = {};
  const cumulativeRevenue = {};
  const margin = {};
  const cumulativeMargin = {};

  const max = estMaxM.value;

  // M0
  revenue[0] = estFirstPrice.value;
  cumulativeRevenue[0] = revenue[0];
  margin[0] = revenue[0] - estAcquisitionCost.value;
  cumulativeMargin[0] = margin[0];

  // M1~Mn
  for (let n = 1; n <= max; n++) {
    const r = rates[n];
    revenue[n] = r == null ? 0 : estFirstPrice.value * r;
    cumulativeRevenue[n] = cumulativeRevenue[n - 1] + revenue[n];
    margin[n] = revenue[n];
    cumulativeMargin[n] = cumulativeMargin[n - 1] + margin[n];
  }

  return { revenue, cumulativeRevenue, margin, cumulativeMargin };
});

/** 回本月份：累计业务毛利首次 >= 0 的 M */
const estBreakevenMonth = computed(() => {
  const cm = estCalcData.value.cumulativeMargin;
  const max = estMaxM.value;
  for (let n = 0; n <= max; n++) {
    if ((cm[n] ?? -1) >= 0) return n;
  }
  return null;
});

/** 从接口加载留存率（按应用） */
const fetchRetentionRates = async () => {
  const appId = searchForm.appId;
  if (appId == null) {
    message.warning("请先选择应用");
    return;
  }
  retentionRateLoading.value = true;
  try {
    const json = await get(
      analyticsUrl("/order/stats/cohort/app-retention-rate/query"),
      { appId }
    );
    if (json.code === 200 && json.data) {
      const d = json.data;
      for (let i = 0; i <= 15; i++) {
        const val = d[`m${i}`];
        estRetentionRates[i] = val != null ? val / 100 : null;
      }
      message.success("留存率已加载");
    } else if (json.code === 200 && json.data === null) {
      // 该应用尚未设置留存率：清空
      for (let i = 0; i <= 15; i++) estRetentionRates[i] = null;
      message.info("该应用尚未设置留存率");
    } else {
      message.warning(
        "获取留存率失败：" + (json.errMsg || json.msg || "未知错误")
      );
    }
  } catch (e) {
    message.error("获取留存率请求失败");
  } finally {
    retentionRateLoading.value = false;
  }
};

/** 保存留存率到接口（按应用） */
const handleSaveRetentionRates = async () => {
  const appId = searchForm.appId;
  if (appId == null) {
    message.warning("请先选择应用");
    return;
  }
  retentionSaving.value = true;
  try {
    const body = { appId };
    for (let i = 0; i <= 15; i++) {
      const v = estRetentionRates[i];
      if (v != null) {
        body[`m${i}`] = +(v * 100).toFixed(2);
      }
    }
    const json = await postJson(
      analyticsUrl("/order/stats/cohort/app-retention-rate/update"),
      body
    );
    if (json.code === 200) {
      message.success("留存率已保存");
    } else {
      message.warning(
        "保存留存率失败：" + (json.errMsg || json.msg || "未知错误")
      );
    }
  } catch (e) {
    message.error("保存留存率请求失败");
  } finally {
    retentionSaving.value = false;
  }
};

/** 数据加载后自动计算 M1 留存率（仅当用户未手动修改时） */
watch(
  () => estStageSums.value,
  () => {
    if (estM1Retention.value == null) {
      estM1Retention.value = calcM1Retention();
    }
  },
  { deep: true }
);

/** 应用切换时自动加载留存率 */
watch(
  () => searchForm.appId,
  (appId) => {
    if (appId != null && isAdmin()) {
      fetchRetentionRates();
    }
  }
);
</script>

<style scoped>
.ltv-table :deep(.ant-table) {
  font-size: 12px;
}

.ltv-table :deep(.ant-table-thead > tr > th) {
  padding: 4px 8px;
  font-size: 12px;
}

.ltv-table :deep(.ant-table-tbody > tr > td) {
  padding: 4px 8px;
  font-size: 12px;
}

/* ===== 折叠标题 ===== */
.ltv-collapse-toggle {
  cursor: pointer;
  user-select: none;
  display: inline-flex;
  align-items: center;
}

.ltv-collapse-toggle:hover {
  color: #1677ff;
}

/* ===== 预估回本表格 ===== */
.estimate-table {
  border-collapse: collapse;
  font-size: 12px;
  min-width: 100%;
}

.estimate-table th,
.estimate-table td {
  padding: 4px 10px;
  white-space: nowrap;
  border: 1px solid #e8e8e8;
}

.estimate-table th {
  background: #fafafa;
  font-weight: 600;
  text-align: center;
}

/* ===== 留存率热力表 ===== */
.retention-card :deep(.ant-card-head) {
  position: relative;
}

.retention-center-title {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: 13px;
  font-weight: 250;
  color: #333;
  pointer-events: none;
}

.retention-card :deep(.ant-card-body) {
  padding: 0;
}

.retention-table-wrap {
  overflow-x: auto;
  overflow-y: auto;
}

.retention-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  white-space: nowrap;
}

.rt-th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f8f9fb;
  padding: 8px 14px;
  text-align: center;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #eceef2;
}

.rt-th.rt-fixed {
  position: sticky;
  left: 0;
  z-index: 3;
  text-align: center;
}

.rt-td {
  padding: 4px 14px;
  text-align: center;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.15s;
}

.rt-rate {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
}

.rt-sub {
  font-size: 11px;
  opacity: 0.55;
  margin-top: 2px;
  font-weight: 400;
}

.rt-td.rt-fixed {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #fff;
  font-weight: 500;
  text-align: center;
  color: #333;
}

.retention-table tbody tr:hover td {
  background: rgba(24, 144, 255, 0.04);
}

.retention-table tbody tr:hover td.rt-fixed {
  background: #f0f7ff;
}
</style>
